import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonInput,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonAlert,
  useIonToast,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatDateTime } from '../utils/format';
import { RefresherEvent } from '../types/ionic';

import { CustomerWithOrders } from 'shared';

interface CustomerFormValues {
  name: string;
}

interface LocationState {
  search?: string;
  customerId?: string;
}

const Customers: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [presentToast] = useIonToast();
  const location = useLocation<LocationState>();

  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [deletingCustomer, setDeletingCustomer] = useState<CustomerWithOrders | null>(null);

  const createForm = useForm<CustomerFormValues>({ defaultValues: { name: '' } });

  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data } = await apiClient.get<CustomerWithOrders[]>('/customers');
      return Array.isArray(data) ? data : [];
    },
  });

  useEffect(() => {
    if (location.state?.search) {
      setSearch(location.state.search);
    }
  }, [location.state?.search]);

  const customers = customersQuery.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredCustomers = useMemo(() => {
    if (!normalizedSearch) {
      return customers;
    }
    return customers.filter((customer) =>
      customer.name?.toLowerCase().includes(normalizedSearch),
    );
  }, [customers, normalizedSearch]);

  useEffect(() => {
    if (!customers.length || !location.state?.customerId) {
      return;
    }
    const match = customers.find((customer) => customer.id === location.state?.customerId);
    if (match) {
      setSearch(match.name);
    }
  }, [customers, location.state?.customerId]);

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await customersQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const invalidateCustomers = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const createCustomerMutation = useMutation({
    mutationFn: async (values: CustomerFormValues) => {
      await apiClient.post('/customers', { name: values.name.trim() });
    },
    onSuccess: () => {
      invalidateCustomers();
      presentToast({
        message: t('customerCreatedSuccessfully') ?? 'Customer created successfully',
        duration: 2000,
        color: 'success',
      });
      createForm.reset();
      setIsCreateModalOpen(false);
    },
    onError: () => {
      presentToast({
        message: t('customerCreateFailed') ?? 'Failed to create customer',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiClient.put(`/customers/${id}`, { name: name.trim() });
    },
    onSuccess: () => {
      invalidateCustomers();
      presentToast({
        message: t('customerUpdatedSuccessfully') ?? 'Customer updated successfully',
        duration: 2000,
        color: 'success',
      });
      setEditingCustomerId(null);
    },
    onError: () => {
      presentToast({
        message: t('customerUpdateFailed') ?? 'Failed to update customer',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/customers/${id}`);
    },
    onSuccess: () => {
      invalidateCustomers();
      presentToast({
        message: t('customerDeletedSuccessfully') ?? 'Customer deleted successfully',
        duration: 2000,
        color: 'success',
      });
      setDeletingCustomer(null);
    },
    onError: () => {
      presentToast({
        message: t('customerDeleteFailed') ?? 'Failed to delete customer',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('customers')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <IonSearchbar
            value={search}
            onIonChange={(event) => setSearch(event.detail.value ?? '')}
            placeholder={t('search') ?? 'Search'}
            debounce={200}
          />

          <IonButton expand="block" style={{ marginBottom: 16 }} onClick={() => setIsCreateModalOpen(true)}>
            {t('addCustomer') ?? 'Add customer'}
          </IonButton>

          {customersQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <IonText color="medium">
              <p>{t('noCustomersYet') ?? t('noData')}</p>
              <p>{t('customersWillAppear') ?? ''}</p>
            </IonText>
          ) : (
            <IonList>
              {filteredCustomers.map((customer) => {
                const isEditing = editingCustomerId === customer.id;
                return (
                  <IonItem key={customer.id} lines="full">
                    <IonLabel>
                      {isEditing ? (
                        <>
                          <IonInput
                            value={editValue}
                            onIonChange={(event) => setEditValue(event.detail.value ?? '')}
                          />
                          <IonText color="medium">
                            <p>
                              {t('firstOrder') ?? 'First order'}:{' '}
                              {customer.firstOrderDate
                                ? formatDateTime(customer.firstOrderDate, 'dd MMM yyyy')
                                : '—'}
                            </p>
                            <p>
                              {t('lastOrder') ?? 'Last order'}:{' '}
                              {customer.lastOrderDate
                                ? formatDateTime(customer.lastOrderDate, 'dd MMM yyyy')
                                : '—'}
                            </p>
                          </IonText>
                        </>
                      ) : (
                        <>
                          <h3 style={{ fontWeight: 600 }}>{customer.name}</h3>
                          <p>
                            {t('firstOrder') ?? 'First order'}:{' '}
                            {customer.firstOrderDate
                              ? formatDateTime(customer.firstOrderDate, 'dd MMM yyyy')
                              : '—'}
                          </p>
                          <p>
                            {t('lastOrder') ?? 'Last order'}:{' '}
                            {customer.lastOrderDate
                              ? formatDateTime(customer.lastOrderDate, 'dd MMM yyyy')
                              : '—'}
                          </p>
                        </>
                      )}
                    </IonLabel>
                    <IonButtons slot="end">
                      {isEditing ? (
                        <>
                          <IonButton
                            size="small"
                            onClick={() => {
                              if (!editValue.trim()) {
                                presentToast({
                                  message: t('enterCustomerName') ?? 'Enter customer name',
                                  duration: 2000,
                                  color: 'warning',
                                });
                                return;
                              }
                              updateCustomerMutation.mutate({
                                id: customer.id,
                                name: editValue,
                              });
                            }}
                            disabled={updateCustomerMutation.isPending}
                          >
                            {t('save')}
                          </IonButton>
                          <IonButton
                            size="small"
                            fill="clear"
                            onClick={() => setEditingCustomerId(null)}
                          >
                            {t('cancel')}
                          </IonButton>
                        </>
                      ) : (
                        <>
                          <IonButton
                            size="small"
                            onClick={() => {
                              setEditingCustomerId(customer.id);
                              setEditValue(customer.name);
                            }}
                          >
                            {t('edit')}
                          </IonButton>
                          <IonButton
                            size="small"
                            color="danger"
                            onClick={() => setDeletingCustomer(customer)}
                          >
                            {t('delete')}
                          </IonButton>
                        </>
                      )}
                    </IonButtons>
                  </IonItem>
                );
              })}
            </IonList>
          )}
        </div>

        <IonModal isOpen={isCreateModalOpen} onDidDismiss={() => setIsCreateModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('addCustomer') ?? 'Add customer'}</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    createForm.reset();
                  }}
                >
                  {t('cancel')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <form
              onSubmit={createForm.handleSubmit((values) => createCustomerMutation.mutate(values))}
            >
              <IonList>
                <IonItem>
                  <IonLabel position="stacked">{t('customerName')}</IonLabel>
                  <Controller
                    control={createForm.control}
                    name="name"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <IonInput
                        value={field.value}
                        onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                      />
                    )}
                  />
                  {createForm.formState.errors.name ? (
                    <IonText color="danger">
                      <p>{t('enterCustomerName') ?? t('enterName') ?? 'Enter name'}</p>
                    </IonText>
                  ) : null}
                </IonItem>
              </IonList>
              <IonButton
                expand="block"
                type="submit"
                style={{ marginTop: 24 }}
                disabled={createCustomerMutation.isPending}
              >
                {createCustomerMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
              </IonButton>
            </form>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={Boolean(deletingCustomer)}
          onDidDismiss={() => setDeletingCustomer(null)}
          header={t('deleteCustomer') ?? 'Delete customer'}
          message={deletingCustomer?.name ?? ''}
          buttons={[
            {
              text: t('cancel'),
              role: 'cancel',
              handler: () => setDeletingCustomer(null),
            },
            {
              text: t('delete'),
              role: 'destructive',
              handler: () => {
                if (deletingCustomer) {
                  deleteCustomerMutation.mutate(deletingCustomer.id);
                }
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Customers;
